import { CalendarClock, Pause, Play, Trash2, ChevronDown, ChevronRight, FileText } from 'lucide-react';
import { useState, useMemo } from 'react';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import yaml from 'js-yaml';

function pad2(n: number) {
  return String(n).padStart(2, '0');
}

function dayName(d: number) {
  const names = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  return names[((d % 7) + 7) % 7] || `Day ${d}`;
}

function ordinal(n: number) {
  const v = n % 100;
  if (v >= 11 && v <= 13) return `${n}th`;
  switch (n % 10) {
    case 1: return `${n}st`;
    case 2: return `${n}nd`;
    case 3: return `${n}rd`;
    default: return `${n}th`;
  }
}

function tryDescribeCron5(expr: string): string | null {
  const parts = String(expr || '').trim().split(/\s+/);
  if (parts.length !== 5) return null;
  const [minRaw, hourRaw, domRaw, monRaw, dowRaw] = parts;

  // Only handle a conservative subset (common presets + simple values/lists).
  const min = minRaw === '*' ? null : Number(minRaw);
  const hour = hourRaw === '*' ? null : Number(hourRaw);
  const dom = domRaw === '*' ? null : Number(domRaw);
  const mon = monRaw === '*' ? null : Number(monRaw);

  const isNum = (n: any) => typeof n === 'number' && Number.isFinite(n);
  const time =
    isNum(hour) && isNum(min) && hour >= 0 && hour <= 23 && min >= 0 && min <= 59
      ? `${pad2(hour)}:${pad2(min)}`
      : null;

  // Hourly: "0 * * * *" (or any fixed minute with hour="*")
  if (time === null && isNum(min) && hourRaw === '*' && domRaw === '*' && monRaw === '*' && dowRaw === '*') {
    return `Every hour at :${pad2(min)}`;
  }

  // Daily: "m h * * *"
  if (time && domRaw === '*' && monRaw === '*' && dowRaw === '*') {
    return `Daily at ${time}`;
  }

  // Weekly: "m h * * dow" (support comma list of dow numbers)
  if (time && domRaw === '*' && monRaw === '*' && dowRaw !== '*') {
    const dows = dowRaw.split(',').map((x) => x.trim()).filter(Boolean);
    const parsed = dows
      .map((x) => (x === '7' ? 0 : Number(x)))
      .filter((x) => Number.isFinite(x) && x >= 0 && x <= 6) as number[];
    if (parsed.length > 0 && parsed.length === dows.length) {
      const days = parsed.map(dayName);
      const dayPart = days.length === 1 ? days[0] : days.slice(0, -1).join(', ') + ' and ' + days[days.length - 1];
      return `Weekly on ${dayPart} at ${time}`;
    }
  }

  // Monthly: "m h dom * *"
  if (time && isNum(dom) && dom >= 1 && dom <= 31 && monRaw === '*' && dowRaw === '*') {
    return `Monthly on the ${ordinal(dom)} at ${time}`;
  }

  // Yearly-ish: "m h dom mon *"
  if (time && isNum(dom) && dom >= 1 && dom <= 31 && isNum(mon) && mon >= 1 && mon <= 12 && dowRaw === '*') {
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `Yearly on ${ordinal(dom)} ${monthNames[mon - 1] || `month ${mon}`} at ${time}`;
  }

  // Specific date + weekday: "m h dom mon dow"
  // Some users/builders include both dom+mon and dow (e.g. "today" schedules).
  if (time && isNum(dom) && dom >= 1 && dom <= 31 && isNum(mon) && mon >= 1 && mon <= 12 && dowRaw !== '*') {
    const dows = dowRaw.split(',').map((x) => x.trim()).filter(Boolean);
    const parsed = dows
      .map((x) => (x === '7' ? 0 : Number(x)))
      .filter((x) => Number.isFinite(x) && x >= 0 && x <= 6) as number[];
    if (parsed.length > 0 && parsed.length === dows.length) {
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const days = parsed.map(dayName);
      const dayPart = days.length === 1 ? days[0] : days.slice(0, -1).join(', ') + ' and ' + days[days.length - 1];
      return `${dayPart}, ${monthNames[mon - 1] || `month ${mon}`} ${ordinal(dom)} at ${time}`;
    }
  }

  return null;
}

interface WorkflowScheduleCardProps {
  schedule: {
    id: string;
    workflowName: string;
    schedule: string;
    enabled: boolean;
    parameters?: Record<string, any>;
    createdBy?: string;
    createdAt?: string;
    lastRun?: string;
    nextRun?: string;
    tenantCode?: string;
  };
  onToggle: () => void;
  onDelete: () => void;
}

export function WorkflowScheduleCard({ schedule, onToggle, onDelete }: WorkflowScheduleCardProps) {
  const [showYaml, setShowYaml] = useState(false);
  
  const statusLabel = schedule.enabled ? 'Enabled' : 'Disabled';
  const statusColors = schedule.enabled
    ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
    : 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400';

  const human = tryDescribeCron5(schedule.schedule);

  const fmt = (iso?: string) => {
    if (!iso) return undefined;
    const ms = Date.parse(iso);
    if (!Number.isFinite(ms)) return iso;
    return new Date(ms).toLocaleString();
  };

  // Filter out internal/hidden parameters
  const hiddenParams = ['runId', 'triggeredBy', 'maxironYamlB64', 'configOverrides'];
  const visibleParameters = schedule.parameters 
    ? Object.entries(schedule.parameters).filter(([key]) => !hiddenParams.includes(key))
    : [];
  const hasVisibleParams = visibleParameters.length > 0;
  
  // Decode and format configuration
  // Helper function to unwrap config by traversing down single-key objects
  const unwrapConfig = (config: any): any => {
    // If not an object, return as-is
    if (!config || typeof config !== 'object' || Array.isArray(config)) {
      return config;
    }
    
    const keys = Object.keys(config);
    
    // If object has multiple keys or no keys, we've reached the content
    if (keys.length !== 1) {
      return config;
    }
    
    // Single key - might be a wrapper, recurse down
    const singleKey = keys[0];
    const value = config[singleKey];
    
    // If value is an object with a single key, continue unwrapping
    if (value && typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length === 1) {
      return unwrapConfig(value);
    }
    
    // Otherwise, this single-key level is the content
    return config;
  };

  const formattedConfig = useMemo(() => {
    if (!schedule.parameters) return null;
    try {
      // Try to get config from maxironYamlB64 (base64 encoded)
      if (schedule.parameters.maxironYamlB64) {
        const decoded = atob(schedule.parameters.maxironYamlB64);
        const parsed = yaml.load(decoded);
        
        // Unwrap the config to show clean content without wrapper paths
        const unwrapped = unwrapConfig(parsed);
        return yaml.dump(unwrapped, { indent: 2, lineWidth: 80 });
      }
      // Fallback to configOverrides if available
      if (schedule.parameters.configOverrides) {
        return schedule.parameters.configOverrides;
      }
      return null;
    } catch (error) {
      console.error('Failed to decode configuration:', error);
      // Fallback to raw configOverrides
      return schedule.parameters.configOverrides || null;
    }
  }, [schedule.parameters]);

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-white dark:bg-gray-800 flex flex-col h-full">
      <div className="flex items-start justify-between mb-3 gap-3">
        <div className="flex-1 min-w-0">
          <h4 className="text-base font-semibold text-gray-900 dark:text-gray-100 truncate">{schedule.workflowName}</h4>
          <p className="text-sm text-gray-500 dark:text-gray-400 truncate">Schedule ID: {schedule.id}</p>
        </div>
        <Badge variant="outline" className={statusColors}>
          <span className="flex items-center gap-1.5">
            <CalendarClock className="w-4 h-4" />
            {statusLabel}
          </span>
        </Badge>
      </div>

      <div className="space-y-2 mb-3">
        <div className="flex items-start gap-2 text-sm">
          <span className="text-gray-500 dark:text-gray-400 shrink-0">Cron:</span>
          <div className="min-w-0">
            <code className="block text-gray-900 dark:text-gray-100 bg-gray-50 dark:bg-gray-900/50 px-2 py-0.5 rounded break-all">
              {schedule.schedule}
            </code>
            {human && (
              <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Runs: {human}
              </div>
            )}
          </div>
        </div>
        {schedule.lastRun && (
          <div className="flex items-center gap-2 text-sm">
            <span className="text-gray-500 dark:text-gray-400 shrink-0">Last run:</span>
            <span className="text-gray-900 dark:text-gray-100">{fmt(schedule.lastRun)}</span>
          </div>
        )}
        {schedule.nextRun && (
          <div className="flex items-center gap-2 text-sm">
            <span className="text-gray-500 dark:text-gray-400 shrink-0">Next run:</span>
            <span className="text-gray-900 dark:text-gray-100">{fmt(schedule.nextRun)}</span>
          </div>
        )}
        {schedule.tenantCode && schedule.tenantCode !== 'portal' && (
          <div className="flex items-center gap-2 text-sm">
            <span className="text-gray-500 dark:text-gray-400 shrink-0">Tenant:</span>
            <span className="text-gray-900 dark:text-gray-100 truncate">{schedule.tenantCode}</span>
          </div>
        )}
        {schedule.createdBy && (
          <div className="flex items-center gap-2 text-sm">
            <span className="text-gray-500 dark:text-gray-400 shrink-0">Created by:</span>
            <span className="text-gray-900 dark:text-gray-100 truncate">{schedule.createdBy}</span>
          </div>
        )}
        {schedule.createdAt && (
          <div className="flex items-center gap-2 text-sm">
            <span className="text-gray-500 dark:text-gray-400 shrink-0">Created:</span>
            <span className="text-gray-900 dark:text-gray-100">{fmt(schedule.createdAt)}</span>
          </div>
        )}
      </div>

      {hasVisibleParams && (
        <div className="mb-3">
          <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Parameters:</p>
          <div className="bg-gray-50 dark:bg-gray-900/50 rounded p-2 text-xs font-mono max-h-24 overflow-auto">
            {visibleParameters.map(([key, value]) => (
              <div key={key} className="flex gap-2">
                <span className="text-gray-600 dark:text-gray-400">{key}:</span>
                <span className="text-gray-900 dark:text-gray-100 break-all">
                  {typeof value === 'string' && value.length > 50 
                    ? `${value.substring(0, 50)}...` 
                    : JSON.stringify(value)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {formattedConfig && (
        <div className="mb-3">
          <button
            onClick={() => setShowYaml(!showYaml)}
            className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
          >
            {showYaml ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            <FileText className="w-4 h-4" />
            Configuration
          </button>
          {showYaml && (
            <div className="bg-gray-900 dark:bg-gray-950 rounded-lg p-4 text-xs font-mono overflow-auto max-h-96 border border-gray-700">
              <pre className="text-gray-100 whitespace-pre-wrap">{formattedConfig}</pre>
            </div>
          )}
        </div>
      )}

      <div className="flex gap-2 mt-auto pt-3 border-t border-gray-200 dark:border-gray-700">
        <Button
          onClick={onToggle}
          variant="outline"
          className="flex-1"
        >
          {schedule.enabled ? (
            <>
              <Pause className="w-4 h-4 mr-2" />
              Disable
            </>
          ) : (
            <>
              <Play className="w-4 h-4 mr-2" />
              Enable
            </>
          )}
        </Button>
        <Button
          onClick={onDelete}
          variant="outline"
          className="text-red-600 hover:text-red-700 dark:text-red-500 dark:hover:text-red-400 border-red-300 hover:border-red-400 dark:border-red-700 dark:hover:border-red-600"
        >
          <Trash2 className="w-4 h-4 mr-2" />
          Delete
        </Button>
      </div>
    </div>
  );
}

